import { Form, Button, Row, Col, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { useCreateMarqueeDataMutation } from "../../../redux/api/MarqueeApi/marqueeApi";
import { FaPlus, FaSave, FaArrowLeft } from "react-icons/fa";
import "./Marquee.css";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import { useFormik } from "formik";
import * as Yup from "yup";

const AddMarquee = () => {
  const navigate = useNavigate();
  const [addMarquee, { isLoading }] = useCreateMarqueeDataMutation();

  const validationSchema = Yup.object().shape({
    content: Yup.string()
      .trim()
      .required("Content is required")
      .min(3, "Content must be at least 3 characters"),
    display_order: Yup.number(),
    status: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      content: "",
      display_order: 0,
      status: true,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await addMarquee(values).unwrap();
        message.success("Marquee created successfully!");
        setTimeout(() => navigate("/admin/marquee"), 2000);
      } catch (error) {
        message.error(
          error.data?.error || error.message || "Failed to create marquee",
        );
      }
    },
  });

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaPlus className="text-primary me-2" />
            Add New Marquee
          </h2>
          <p className="text-muted mb-0">
            Create a new announcement for the website banner
          </p>
        </div>
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-4"
          onClick={() => navigate("/admin/marquee")}
        >
          <FaArrowLeft /> Back to List
        </Button>
      </div>

      <Form onSubmit={formik.handleSubmit}>
        <Card className="border-0 overflow-hidden">
          <Card.Body className="p-4 p-md-5">
            <h5 className="mb-4 text-secondary border-start border-4 border-primary ps-3">
              Marquee Information
            </h5>
            <Row className="g-4">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Marquee Content <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="content"
                    {...formik.getFieldProps("content")}
                    isInvalid={
                      formik.touched.content && !!formik.errors.content
                    }
                    placeholder="Enter announcement text here..."
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.content}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="display_order"
                    {...formik.getFieldProps("display_order")}
                  />
                  <Form.Text className="text-muted small">
                    Lower numbers appear first in the marquee.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6} className="d-flex align-items-center mt-md-auto mt-4">
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="marquee-status"
                    label={
                      formik.values.status
                        ? "Enabled / Active"
                        : "Disabled / Inactive"
                    }
                    name="status"
                    checked={formik.values.status}
                    onChange={formik.handleChange}
                    className={`fw-bold ${
                      formik.values.status ? "text-success" : "text-danger"
                    }`}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-3 mt-5">
              <Button
                variant="light"
                className="rounded-pill px-4"
                onClick={() => navigate("/admin/marquee")}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="rounded-pill px-5 shadow-sm d-flex align-items-center gap-2"
              >
                <FaSave /> Save Marquee
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
};

export default AddMarquee;
